/**
 * Learning Center API: the learner's external certificates (plan
 * docs/plans/lms-plan.md 5.5 lms_external_certs, 7.5, R17, Appendix A
 * "Sign-offs and external certificates"; 18.5, 18.15; WP-34's routes, WP-41's
 * client). The admin review queue and the review post are WP-45's (Team,
 * External certificates).
 *
 *   POST /me/external-certs   multipart: file, title, issuer, issued_on,
 *                             expires_on?, credential_id?, course_id?
 *                             -> the row, status 'pending' (201)
 *   GET  /me/external-certs   -> { items, page } newest first
 *   GET  /external-certs/:id/url?disposition=inline|attachment
 *                             -> { url, filename } (10 minutes; own, or an
 *                                admin who reaches the holder; 404 otherwise, 18.5)
 *
 * Uploads (2.3a): PDF, PNG or JPEG up to 25 MB; the server checks the type,
 * the size and the file's own bytes, so the checks here are only a courtesy.
 * The FormData is posted with no explicit Content-Type: the browser sets the
 * multipart boundary itself.
 */
import api from '../../services/api';
import type { LmsList } from './client';

/** 5.5 lms_external_certs.mime: the upload allow-list. */
export const EXTERNAL_CERT_MIMES = ['application/pdf', 'image/png', 'image/jpeg'] as const;

/** 2.3a "Uploads": 25 MB. */
export const EXTERNAL_CERT_MAX_BYTES = 25 * 1024 * 1024;

export type ExternalCertStatus = 'pending' | 'accepted' | 'rejected';

/** A row as its owner sees it (WP-34 publicRow: the storage key never leaves the server). */
export interface ExternalCert {
  id: string;
  user_id: string;
  title: string;
  issuer: string;
  issued_on: string;
  expires_on: string | null;
  credential_id: string | null;
  filename: string;
  mime: string;
  size_bytes: number;
  course_id: string | null;
  course: { id: string; title: string; slug: string } | null;
  status: ExternalCertStatus;
  reviewed_by?: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  created_at: string;
}

/** The form's fields (7.5); dates are YYYY-MM-DD. */
export interface ExternalCertUpload {
  file: File;
  title: string;
  issuer: string;
  issued_on: string;
  expires_on?: string | null;
  credential_id?: string | null;
  course_id?: string | null;
}

const enc = encodeURIComponent;

/** The multipart body of POST /me/external-certs (optional fields are left out when empty). */
export function buildExternalCertForm(input: ExternalCertUpload): FormData {
  const form = new FormData();
  form.append('file', input.file);
  form.append('title', input.title);
  form.append('issuer', input.issuer);
  form.append('issued_on', input.issued_on);
  if (input.expires_on) form.append('expires_on', input.expires_on);
  if (input.credential_id) form.append('credential_id', input.credential_id);
  if (input.course_id) form.append('course_id', input.course_id);
  return form;
}

/** POST /api/lms/me/external-certs (multipart) -> the pending row. */
export async function uploadExternalCert(input: ExternalCertUpload): Promise<ExternalCert> {
  const response = await api.post<ExternalCert>('/api/lms/me/external-certs', buildExternalCertForm(input));
  return response.data;
}

/** GET /api/lms/me/external-certs */
export async function listMyExternalCerts(options: { limit?: number; offset?: number } = {}): Promise<LmsList<ExternalCert>> {
  const params: Record<string, number> = {};
  if (options.limit !== undefined) params.limit = options.limit;
  if (options.offset !== undefined) params.offset = options.offset;
  const response = await api.get<LmsList<ExternalCert>>('/api/lms/me/external-certs', { params });
  return response.data;
}

/** GET /api/lms/external-certs/:id/url (inline by default: the learner views the file). */
export async function getExternalCertUrl(
  id: string,
  disposition: 'inline' | 'attachment' = 'inline'
): Promise<{ url: string; filename: string }> {
  const response = await api.get<{ url: string; filename: string }>(`/api/lms/external-certs/${enc(id)}/url`, {
    params: { disposition },
  });
  return response.data;
}
